import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FolderKanban, Link2, CheckCircle2, XCircle, Building, Search, Loader2, ExternalLink, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function AdminProjects() {
  const [searchParams] = useSearchParams();
  const [zohoConnected, setZohoConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portals, setPortals] = useState([]);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assigningProject, setAssigningProject] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter projects by search
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter(p => 
      (p.name || '').toLowerCase().includes(query) ||
      (p.description || '').toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  useEffect(() => {
    // Check if we just connected Zoho
    if (searchParams.get('zoho_connected') === 'true') {
      toast.success('Zoho Projects connected successfully!');
    }
    checkZohoStatus();
    fetchCompanies();
    fetchAssignments();
  }, [searchParams]);

  const checkZohoStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setZohoConnected(data.connected);
        if (data.connected) {
          fetchPortals();
        }
      }
    } catch (error) {
      console.error('Failed to check Zoho status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho/portals`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPortals(data.portals || []);
        // Auto-select first portal
        if (data.portals && data.portals.length > 0) {
          handleSelectPortal(data.portals[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch portals:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/companies`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/projects/assignments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    }
  };

  const handleSelectPortal = async (portal) => {
    setSelectedPortal(portal);
    setLoadingProjects(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho/portals/${portal.id}/projects`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to fetch projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const connectZoho = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/zoho/auth-url`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Redirect to Zoho OAuth
        window.location.href = data.auth_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (error) {
      toast.error('Failed to connect to Zoho');
    }
  };

  const handleAssignProject = async (project) => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    try {
      // Use id_string to avoid JavaScript integer precision issues with large Zoho IDs
      const projectId = project.id_string || String(project.id);
      const portalId = selectedPortal.id_string || String(selectedPortal.id);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/projects/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          zoho_project_id: projectId,
          zoho_portal_id: portalId,
          project_name: project.name,
          project_status: project.status || project.status_name,
          start_date: project.start_date,
          end_date: project.end_date,
          company_id: selectedCompany
        })
      });

      if (response.ok) {
        toast.success(`Project assigned successfully`);
        setAssigningProject(null);
        setSelectedCompany('');
        fetchAssignments();
        // Refresh projects to update assignment status
        handleSelectPortal(selectedPortal);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign project');
      }
    } catch (error) {
      toast.error('Failed to assign project');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/projects/assignments/${assignmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Assignment removed');
        fetchAssignments();
        if (selectedPortal) {
          handleSelectPortal(selectedPortal);
        }
      } else {
        toast.error('Failed to remove assignment');
      }
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Projects">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Projects">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <FolderKanban className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-mono font-bold mb-2">Zoho Projects Integration</h2>
                <p className="text-sm text-muted-foreground">
                  Connect to Zoho Projects and assign projects to your customers.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {zohoConnected ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-mono">
                  <CheckCircle2 className="h-4 w-4" />
                  Connected
                </span>
              ) : (
                <Button onClick={connectZoho} className="bg-primary text-primary-foreground">
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect Zoho
                </Button>
              )}
            </div>
          </div>
        </div>

        {!zohoConnected ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Link2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">Connect Zoho Projects</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Connect your Zoho Projects account to import and assign projects to your customers.
            </p>
            <Button onClick={connectZoho} className="bg-primary text-primary-foreground">
              <ExternalLink className="h-4 w-4 mr-2" />
              Connect to Zoho Projects
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Projects from Zoho */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-mono font-bold">Zoho Projects</h3>
                {selectedPortal && (
                  <Button variant="outline" size="sm" onClick={() => handleSelectPortal(selectedPortal)}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                )}
              </div>

              {/* Portal Selection */}
              {portals.length > 1 && (
                <div>
                  <Label className="font-mono text-sm">Select Portal</Label>
                  <select
                    value={selectedPortal?.id || ''}
                    onChange={(e) => {
                      const portal = portals.find(p => String(p.id) === e.target.value);
                      if (portal) handleSelectPortal(portal);
                    }}
                    className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md font-mono text-sm"
                  >
                    {portals.map((portal) => (
                      <option key={portal.id} value={portal.id}>
                        {portal.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Projects List */}
              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[500px] overflow-y-auto">
                {loadingProjects ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    {searchQuery ? 'No matching projects found' : 'No projects found'}
                  </div>
                ) : (
                  filteredProjects.map((project) => (
                    <div key={project.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{project.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-mono ${
                              project.is_assigned 
                                ? 'bg-primary/10 text-primary' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {project.is_assigned ? 'Assigned' : project.status || project.status_name || 'Active'}
                            </span>
                          </div>
                          {project.owner_name && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Owner: {project.owner_name}
                            </p>
                          )}
                        </div>
                        {!project.is_assigned && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setAssigningProject(project)}
                          >
                            Assign
                          </Button>
                        )}
                      </div>

                      {/* Assignment Form */}
                      {assigningProject?.id === project.id && (
                        <div className="mt-4 p-4 bg-background rounded-md border border-border">
                          <Label className="font-mono text-sm">Assign to Company</Label>
                          <select
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                            className="w-full mt-1 px-3 py-2 bg-card border border-border rounded-md font-mono text-sm"
                          >
                            <option value="">Select a company...</option>
                            {companies.map((company) => (
                              <option key={company.company_id} value={company.company_id}>
                                {company.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              onClick={() => handleAssignProject(project)}
                              disabled={!selectedCompany}
                            >
                              Confirm
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setAssigningProject(null);
                                setSelectedCompany('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Current Assignments */}
            <div className="space-y-4">
              <h3 className="text-lg font-mono font-bold">Project Assignments</h3>
              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[600px] overflow-y-auto">
                {assignments.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No projects assigned yet</p>
                    <p className="text-sm mt-1">Assign projects from Zoho to your customers</p>
                  </div>
                ) : (
                  assignments.map((assignment) => (
                    <div key={assignment.assignment_id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{assignment.project_name}</h4>
                          <div className="flex items-center gap-2 mt-2">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-primary">{assignment.company_name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveAssignment(assignment.assignment_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminProjects;
